import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Crosshair, Link2, MapPin, Navigation } from 'lucide-react';
import { Input } from '../ui/Input';
import { useToast } from '../ui/Toaster';
import { apiService } from '../../services/api';

declare global {
  interface Window {
    google?: any;
    __googleMapsLoadPromise?: Promise<void>;
  }
}

type Suggestion = { placeId: string; label: string };

export interface AddressSelection {
  address: string;
  location: string;
  latitude: number;
  longitude: number;
  placeId: string;
  geocodeProvider: string;
  geocodeConfidence: number;
  addressLine1?: string;
  city?: string;
  county?: string;
  country?: string;
  postalCode?: string;
  /** When false, parent should not persist (auto-locate / initial seed). */
  userInitiated?: boolean;
}

interface AddressLocationPickerProps {
  propertyId?: string;
  showShareLinkActions?: boolean;
  /** When true, notify parent of lat/lng props once on mount (for pin-link submit pages). */
  autoEmitInitialSelection?: boolean;
  /** Skip browser geolocation on mount (e.g. editing a property with saved coords). */
  skipAutoLocate?: boolean;
  value: string;
  latitude?: number | null;
  longitude?: number | null;
  countryCode?: string;
  onAddressChange: (value: string) => void;
  onLocationSelect: (value: AddressSelection) => void;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SearchWrap = styled.div`
  position: relative;
`;

const SuggestionList = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 6;
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.popover};
  border-radius: 10px;
  box-shadow: ${(p) => p.theme.shadowSoft};
  overflow: hidden;
`;

const SuggestionItem = styled.button`
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  color: ${(p) => p.theme.foreground};
  padding: 10px 12px;
  font-size: 13px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.muted};
  }
`;

const MapCard = styled.div`
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 12px;
  overflow: hidden;
`;

const MapHeader = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.card};
  font-size: 12px;
  color: ${(p) => p.theme.mutedForeground};
`;

const MapActionsRow = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.background};
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MiniActions = styled.button`
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.background};
  color: ${(p) => p.theme.foreground};
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const MapView = styled.div`
  height: 260px;
  width: 100%;
`;

const MissingApiKeyState = styled.div`
  border-top: 1px solid ${(p) => p.theme.border};
  padding: 12px;
  font-size: 12px;
  color: ${(p) => p.theme.mutedForeground};
  background: ${(p) => p.theme.background};
`;

const MetaRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 8px 12px 10px;
  border-top: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.background};
  font-size: 12px;
  color: ${(p) => p.theme.mutedForeground};
`;

function parseAddressParts(addressComponents: any[] = []) {
  const findType = (...types: string[]) =>
    addressComponents.find((component) => types.some((type) => component.types.includes(type)));
  const streetNumber = findType('street_number')?.long_name || '';
  const route = findType('route')?.long_name || '';
  const locality = findType('locality', 'sublocality', 'administrative_area_level_3')?.long_name || '';
  const county = findType('administrative_area_level_2', 'administrative_area_level_1')?.long_name || '';
  const country = findType('country')?.short_name || '';
  const postalCode = findType('postal_code')?.long_name || '';

  return {
    addressLine1: `${streetNumber} ${route}`.trim(),
    city: locality,
    county,
    country,
    postalCode,
  };
}

function computeConfidence(geometry?: any | null) {
  if (!geometry?.location_type) return 0.7;
  if (geometry.location_type === 'ROOFTOP') return 0.95;
  if (geometry.location_type === 'RANGE_INTERPOLATED') return 0.85;
  if (geometry.location_type === 'GEOMETRIC_CENTER') return 0.7;
  return 0.55;
}

function mapResultToSelection(result: any, lat: number, lng: number): AddressSelection {
  const parts = parseAddressParts(result.address_components || []);
  const location = [parts.city, parts.county, parts.country].filter(Boolean).join(', ') || result.formatted_address;
  return {
    address: result.formatted_address || '',
    location,
    latitude: lat,
    longitude: lng,
    placeId: result.place_id || '',
    geocodeProvider: 'GOOGLE_GEOCODING',
    geocodeConfidence: computeConfidence(result.geometry),
    addressLine1: parts.addressLine1 || undefined,
    city: parts.city || undefined,
    county: parts.county || undefined,
    country: parts.country || undefined,
    postalCode: parts.postalCode || undefined,
  };
}

const AddressLocationPicker: React.FC<AddressLocationPickerProps> = ({
  propertyId,
  showShareLinkActions = true,
  autoEmitInitialSelection = false,
  skipAutoLocate = false,
  value,
  latitude,
  longitude,
  countryCode = 'KE',
  onAddressChange,
  onLocationSelect,
}) => {
  const toast = useToast();
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const hasSavedCoordinates = typeof latitude === 'number' && typeof longitude === 'number';
  const defaultCenter = { lat: -1.286389, lng: 36.817223 };
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [linkStatus, setLinkStatus] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copiedAt, setCopiedAt] = useState<Date | null>(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [selected, setSelected] = useState<{ lat: number; lng: number }>(() =>
    hasSavedCoordinates ? { lat: latitude!, lng: longitude! } : defaultCenter,
  );
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const geocoderRef = useRef<any>(null);
  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const autoLocationRequestedRef = useRef(false);
  const initialSelectionEmittedRef = useRef(false);
  const mapInitializedRef = useRef(false);
  const darkMapStyles = useRef<any[]>([
    { elementType: 'geometry', stylers: [{ color: '#1d1f26' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#1d1f26' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#8f98a5' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2f3a' }] },
    { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#2f3642' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#394355' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#12161f' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#242934' }] },
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#252b38' }] },
  ]);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    if (typeof latitude === 'number' && typeof longitude === 'number') {
      setSelected({ lat: latitude, lng: longitude });
    }
  }, [latitude, longitude]);

  useEffect(() => {
    autoLocationRequestedRef.current = false;
    initialSelectionEmittedRef.current = false;
    mapInitializedRef.current = false;
    mapInstanceRef.current = null;
    markerRef.current = null;
  }, [propertyId]);

  // Pin-link pages: seed parent selection from saved coords so Submit works without re-clicking the map
  useEffect(() => {
    if (!autoEmitInitialSelection || initialSelectionEmittedRef.current) return;
    if (typeof latitude !== 'number' || typeof longitude !== 'number') return;
    initialSelectionEmittedRef.current = true;
    const addressText =
      value.trim() ||
      `Pinned location (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`;
    onLocationSelect({
      address: addressText,
      location: addressText,
      latitude,
      longitude,
      placeId: '',
      geocodeProvider: 'GROUND_PIN_LINK',
      geocodeConfidence: 0.8,
      userInitiated: false,
    });
  }, [autoEmitInitialSelection, latitude, longitude, value, onLocationSelect]);

  useEffect(() => {
    if (!googleMapsApiKey || window.google?.maps) {
      setMapsReady(Boolean(window.google?.maps));
      return;
    }

    if (!window.__googleMapsLoadPromise) {
      window.__googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google Maps script'));
        document.head.appendChild(script);
      });
    }

    window.__googleMapsLoadPromise
      .then(() => setMapsReady(true))
      .catch(() => setMapsReady(false));
  }, [googleMapsApiKey]);

  useEffect(() => {
    if (!mapsReady || !mapRef.current || !window.google?.maps || mapInitializedRef.current) return;
    mapInitializedRef.current = true;
    const center = { lat: selected.lat, lng: selected.lng };
    const googleMap = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 16,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: darkMapStyles.current,
    });
    const marker = new window.google.maps.Marker({
      map: googleMap,
      position: center,
      draggable: true,
    });
    const geocoder = new window.google.maps.Geocoder();
    const autocomplete = new window.google.maps.places.AutocompleteService();
    const placesService = new window.google.maps.places.PlacesService(googleMap);

    marker.addListener('dragend', () => {
      const next = marker.getPosition();
      if (!next) return;
      reverseGeocode(next.lat(), next.lng(), true);
    });

    googleMap.addListener('click', (event: any) => {
      if (!event.latLng) return;
      reverseGeocode(event.latLng.lat(), event.latLng.lng(), true);
    });

    mapInstanceRef.current = googleMap;
    markerRef.current = marker;
    geocoderRef.current = geocoder;
    autocompleteServiceRef.current = autocomplete;
    placesServiceRef.current = placesService;
  }, [mapsReady, selected.lat, selected.lng]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    const next = new window.google!.maps.LatLng(selected.lat, selected.lng);
    map.panTo(next);
    marker.setPosition(next);
  }, [selected.lat, selected.lng]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3 || !mapsReady || !autocompleteServiceRef.current || !window.google?.maps.places) {
      setSuggestions([]);
      return;
    }

    const timer = window.setTimeout(() => {
      setLoading(true);
      autocompleteServiceRef.current!.getPlacePredictions(
        {
          input: q,
          componentRestrictions: { country: countryCode.toLowerCase() },
          types: ['address'],
        },
        (predictions: any[] | null, status: any) => {
          if (status !== window.google!.maps.places.PlacesServiceStatus.OK || !predictions) {
            setSuggestions([]);
            setLoading(false);
            return;
          }
          setSuggestions(predictions.slice(0, 5).map((p) => ({ placeId: p.place_id, label: p.description })));
          setLoading(false);
        },
      );
    }, 260);

    return () => {
      window.clearTimeout(timer);
    };
  }, [query, countryCode, mapsReady]);

  const reverseGeocode = useCallback(async (lat: number, lng: number, userInitiated = false) => {
    setSelected({ lat, lng });
    const map = mapInstanceRef.current;
    const marker = markerRef.current;
    if (map && marker && window.google?.maps) {
      const next = new window.google.maps.LatLng(lat, lng);
      map.panTo(next);
      marker.setPosition(next);
    }

    if (!geocoderRef.current) {
      onLocationSelect({
        address: query,
        location: query,
        latitude: lat,
        longitude: lng,
        placeId: '',
        geocodeProvider: 'GOOGLE_GEOCODING',
        geocodeConfidence: 0.5,
        userInitiated,
      });
      return;
    }

    try {
      const result = await new Promise<any | null>((resolve) => {
        geocoderRef.current!.geocode({ location: { lat, lng } }, (results: any[] | null, status: any) => {
          if (status !== 'OK' || !results || results.length === 0) {
            resolve(null);
            return;
          }
          resolve(results[0]);
        });
      });

      if (!result) throw new Error('No reverse geocode results');
      const mapped = mapResultToSelection(result, lat, lng);
      onAddressChange(mapped.address);
      onLocationSelect({ ...mapped, userInitiated });
    } catch {
      onLocationSelect({
        address: query,
        location: query,
        latitude: lat,
        longitude: lng,
        placeId: '',
        geocodeProvider: 'GOOGLE_GEOCODING',
        geocodeConfidence: 0.5,
        userInitiated,
      });
    }
  }, [onAddressChange, onLocationSelect, query]);

  const requestDeviceLocation = useCallback((isAutomatic = false) => {
    if (!navigator.geolocation) {
      setLocationError('Device location is not supported in this browser.');
      return;
    }
    setLocating(true);
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        reverseGeocode(position.coords.latitude, position.coords.longitude, !isAutomatic);
      },
      (error) => {
        setLocating(false);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationError(
            isAutomatic
              ? 'Allow location access to auto-fill your current position.'
              : 'Location permission denied. Allow access and try again.',
          );
          return;
        }
        if (error.code === error.TIMEOUT) {
          setLocationError('Location request timed out. Try again or pick a point on the map.');
          return;
        }
        setLocationError('Unable to get your current location. Try again.');
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 60000,
      },
    );
  }, [reverseGeocode]);

  useEffect(() => {
    if (!mapsReady || autoLocationRequestedRef.current || skipAutoLocate) return;
    if (hasSavedCoordinates) return;
    autoLocationRequestedRef.current = true;
    requestDeviceLocation(true);
  }, [hasSavedCoordinates, latitude, longitude, mapsReady, requestDeviceLocation, skipAutoLocate]);

  const handleSuggestionSelect = async (item: Suggestion) => {
    if (!placesServiceRef.current || !window.google?.maps.places) return;
    setSuggestions([]);
    setLoading(true);
    try {
      const place = await new Promise<any | null>((resolve) => {
        placesServiceRef.current!.getDetails(
          {
            placeId: item.placeId,
            fields: ['geometry', 'formatted_address', 'address_components', 'place_id'],
          },
          (result: any, status: any) => {
            if (status !== window.google!.maps.places.PlacesServiceStatus.OK || !result?.geometry?.location) {
              resolve(null);
              return;
            }
            resolve(result);
          },
        );
      });
      if (!place?.geometry?.location || !place.formatted_address) throw new Error('No place details');

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const fakeGeocodeResult: any = {
        address_components: place.address_components || [],
        formatted_address: place.formatted_address,
        geometry: {
          location: place.geometry.location,
          location_type: 'ROOFTOP',
          viewport: place.geometry.viewport!,
        },
        place_id: place.place_id || item.placeId,
        types: [],
      };
      const mapped = mapResultToSelection(fakeGeocodeResult, lat, lng);
      setSelected({ lat, lng });
      onAddressChange(mapped.address);
      onLocationSelect({ ...mapped, userInitiated: true });
    } finally {
      setLoading(false);
    }
  };

  const openGoogleMaps = () => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${selected.lat},${selected.lng}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const generateGroundPinLink = useCallback(async () => {
    if (!propertyId) {
      setLinkStatus('Save the property first, then generate a temporary pin link from Edit mode.');
      return;
    }
    setGeneratingLink(true);
    setLinkStatus('');
    try {
      const response = await apiService.createPropertyLocationPinLink(propertyId);
      const link = response.data?.link;
      if (!link) throw new Error('No link returned');
      setGeneratedLink(link);
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setCopiedAt(new Date());
        setLinkStatus('Temporary pin link copied. Share it with the on-site person.');
        toast.success('Temporary pin link copied', {
          description:
            'Share it with the on-site person: open link, allow location, adjust pin, and submit.',
        });
      } else {
        setLinkStatus(`Temporary pin link: ${link}`);
        toast.info('Temporary pin link generated', {
          description: 'Share the link with the on-site person to pin and submit the exact location.',
        });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string; message?: string }; status?: number } };
      const apiMsg = axiosErr.response?.data?.error || axiosErr.response?.data?.message;
      const status = axiosErr.response?.status;
      if (status === 403) {
        setLinkStatus('You do not have permission to generate location pin links for this property.');
      } else if (status === 401) {
        setLinkStatus('Sign in again to generate a location pin link.');
      } else {
        setLinkStatus(apiMsg || 'Failed to generate temporary pin link. Try again.');
      }
    } finally {
      setGeneratingLink(false);
    }
  }, [propertyId, toast]);

  return (
    <Wrap>
      <SearchWrap>
        <Input
          id="drw-addr"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onAddressChange(e.target.value);
          }}
          placeholder="Search street address..."
        />
        {suggestions.length > 0 && (
          <SuggestionList>
            {suggestions.map((s) => (
              <SuggestionItem type="button" key={s.placeId} onClick={() => handleSuggestionSelect(s)}>
                <MapPin size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{s.label}</span>
              </SuggestionItem>
            ))}
          </SuggestionList>
        )}
      </SearchWrap>

      <MapCard>
        <MapHeader>
          {loading
            ? 'Finding addresses...'
            : locating
              ? 'Detecting your current location...'
              : 'Drag marker or click map to pin exact location'}
        </MapHeader>
        <MapActionsRow>
          {showShareLinkActions && propertyId && (
            <MiniActions type="button" onClick={generateGroundPinLink} disabled={generatingLink}>
              <Link2 size={12} />
              {generatingLink ? 'Generating link...' : generatedLink ? 'Regenerate temporary pin link' : 'Generate temporary pin link'}
            </MiniActions>
          )}
          {showShareLinkActions && !propertyId && (
            <MiniActions type="button" disabled title="Save the property first">
              <Link2 size={12} />
              Save property to generate pin link
            </MiniActions>
          )}
          {showShareLinkActions && generatedLink && (
            <MiniActions
              type="button"
              onClick={() => window.open(generatedLink, '_blank', 'noopener,noreferrer')}
            >
              <Navigation size={12} />
              Open link
            </MiniActions>
          )}
          <MiniActions type="button" onClick={() => requestDeviceLocation()} disabled={locating}>
            <Crosshair size={12} />
            {locating ? 'Locating...' : 'Use my location'}
          </MiniActions>
          {mapsReady && (
            <MiniActions type="button" onClick={openGoogleMaps}>
              <Navigation size={12} />
              Verify on Google Maps
            </MiniActions>
          )}
        </MapActionsRow>
        {mapsReady ? (
          <MapView ref={mapRef} />
        ) : (
          <MissingApiKeyState>
            Google Maps failed to load. Set `REACT_APP_GOOGLE_MAPS_API_KEY` and refresh to enable pinning.
          </MissingApiKeyState>
        )}
        <MetaRow>
          <div>Lat: {selected.lat.toFixed(6)}</div>
          <div>
            {locationError ||
              linkStatus ||
              (copiedAt
                ? `Copied at ${copiedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                : `Lng: ${selected.lng.toFixed(6)}`)}
          </div>
        </MetaRow>
      </MapCard>
    </Wrap>
  );
};

export default AddressLocationPicker;
